/* global Cropper */

import { Controller } from "@hotwired/stimulus";
import { blobToBase64, urlToBase64 } from "./site_builder/customization/lib";
import { memoizeFIFO } from "./helpers/memoize";
import { persistCropperStateToDOM } from "./mixins/setup_image_cropper";

// Connects to data-controller="cropper"
export default class extends Controller {
  static targets = ["image", "zoom", "filename"];
  static values = {
    options: Object,
    validation: Object,
  }

  connect() {
    this.zoomValue = 0;
    this.baseRatio = 1;
    this.#setCroppingEvents();

    this.loader = this.element.querySelector('.cropper__loader');
    this.urlToBase64Store = memoizeFIFO(urlToBase64, { size: 3 });
  }

  zoomIn() {
    this.#updateZoom(+1);
  }

  zoomOut() {
    this.#updateZoom(-1);
  }

  #updateZoom = (step = 0) => {
    const newSliderVal = parseInt(this.zoomTarget.value, 10) + step;
    this.zoomTarget.value = newSliderVal;

    const scale = Math.max(1 + (newSliderVal / 10), 0.1);
    const ratio = this.baseRatio * scale;

    const box = this.cropper.getCropBoxData();
    const pivot = {
      x: box.left + box.width / 2,
      y: box.top + box.height / 2
    };

    this.cropper.zoomTo(ratio, pivot);
  }

  async use(url, filename) {
    this.originalImageUrl = url;
    this.filenameTarget.textContent = filename || url.split('/').pop().split('?')[0];
    if(this.loader) {
      this.loader.classList.add('show');
    }
    await this.#setImage(url);
  }

  apply() {
    const activeTab = document.querySelector('.modal-content .tabbed .tabbed__item.active').getAttribute('data-tabs-view-param');

    persistCropperStateToDOM({
      containerSelector: `.image-container[data-site-builder--custom-images--actions-key-value="${document.body.dataset.imageType}"] .image-view`,
      cropperInstance: this,
      imageUrl: (this.originalImageUrl && this.originalImageUrl.startsWith('blob:')) ? this.imageTarget.src : this.originalImageUrl,
      imageName: this.filenameTarget.textContent,
      hiddenDivClass: 'image-cropper-details',
      activetab: activeTab,
    });
    this.dispatch('done');
  }

  remove({destroyCropper = true}) {
    if (destroyCropper) this.cropper.destroy();

    this.imageTarget.src = ""
    this.dispatch('exit');
  }

  async #setImage(imageUrl) {
    // Hide image until fully ready
    if (this.imageTarget) this.imageTarget.style.opacity = '0';

    if (imageUrl.startsWith('blob:')) {
      this.imageTarget.src = await blobToBase64(imageUrl);
    } else {
      this.imageTarget.src = await this.urlToBase64Store(imageUrl)
    }
  }

  #setCroppingEvents() {
    this.imageTarget.onload = () => {
      this.cropper?.destroy();
      this.cropper = new Cropper(this.imageTarget, this.optionsValue);

      this.originalImageWidth = this.imageTarget.naturalWidth;
      this.originalImageHeight = this.imageTarget.naturalHeight;

      this.imageTarget.addEventListener(
        "ready",
        this.#handleCropperReady,
        { once: true }
      );
    };

    this.zoomTarget.addEventListener("input", () => this.#updateZoom(0));
  }

  #handleCropperReady = () => {
    const canvas = this.cropper.getCanvasData();

    this.baseRatio = (canvas?.naturalWidth && canvas.naturalWidth !== 0)
      ? canvas.width / canvas.naturalWidth
      : 1;

    this.zoomTarget.value = 0;
    this.zoomValue = 0;

    this.imageTarget.addEventListener("zoom", this.#handleZoomEvent);

    if(this.loader) {
      this.loader.classList.remove("show");
    }
    // Reveal the image once fully ready
    if (this.imageTarget) this.imageTarget.style.opacity = '1';
    this.dispatch("load");

    this.originalData = this.cropper.getData();
  };

  #handleZoomEvent = (event) => {
    const { ratio } = event.detail;
    if (!ratio) return;

    const sliderValue = this.#calculateSliderValue(ratio);
    this.zoomTarget.value = sliderValue;

    const minRatio = this.baseRatio * (1 + parseInt(this.zoomTarget.min, 10) / 10);
    const maxRatio = this.baseRatio * (1 + parseInt(this.zoomTarget.max, 10) / 10);

    if (ratio < minRatio || ratio > maxRatio) {
      event.preventDefault();
    }
  };

  #calculateSliderValue = (ratio) => {
    const rawValue = ((ratio / this.baseRatio) - 1) * 10;
    const min = parseInt(this.zoomTarget.min, 10);
    const max = parseInt(this.zoomTarget.max, 10);
    return Math.max(min, Math.min(max, rawValue));
  };

  hasCropped(tolerance = 0.0001) {
    const currentData = this.cropper.getData();
    for (const [key, value] of Object.entries(this.originalData)) {
      if (Math.abs(value - currentData[key]) > tolerance) {
        return true;
      }
    }
    return false;
  }

  getImageMimeType() {
    return this.imageTarget.src.match(/^data:(image\/[^;]+);/)?.[1];
  }

  #getScaledDimensions(width, height) {
    const maxDimensions = this.validationValue?.maxDimensions;
    const MAX_WIDTH = maxDimensions.width;
    const MAX_HEIGHT = maxDimensions.height;

    if (width <= MAX_WIDTH && height <= MAX_HEIGHT) {
      return { width, height };
    }

    const widthScale = MAX_WIDTH / width;
    const heightScale = MAX_HEIGHT / height;
    const scale = Math.min(widthScale, heightScale);

    return {
      width: Math.round(width * scale),
      height: Math.round(height * scale)
    };
  }

  getImage({ name, ...options }) {
    return new Promise((resolve) => {
      const mimeType = this.getImageMimeType();
      const scaledDimensions = this.#getScaledDimensions(
        this.originalImageWidth,
        this.originalImageHeight
      );

      const canvasOptions = {
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
        width: scaledDimensions.width,
        height: scaledDimensions.height,
        ...options
      };

      if (mimeType?.includes('jpeg') || mimeType?.includes('jpg')) {
        canvasOptions.fillColor = '#fff';
      }

      const canvas = this.cropper.getCroppedCanvas(canvasOptions);

      canvas.toBlob((blob) => {
        const file = new File([blob], name, { type: blob.type });
        resolve(file);
      }, mimeType);
    })
  }

  disconnect() {
    this.urlToBase64Store = undefined;
  }
}
